import React from 'react';
import { Layout, Header } from 'app/theme/layout'
import { Helmet } from 'react-helmet-async'
import Calculator from 'app/theme/calculator'
import HowItWorks from 'app/theme/sections/HowItWorks'
import HowItWorksLarge from 'app/theme/sections/HowItWorksLarge'
import WhyWorkWithUs from 'app/theme/sections/WhyWorkWithUs'
import FeaturedClients from 'app/theme/sections/FeaturedClients'
import OccasionsSlider from 'app/theme/sections/CateringOccasionsSlider'
import CuisineTypes from 'app/theme/sections/CuisineTypes'
import { OccasionPageGrid, OccasionPageGridRow } from './shared/OccasionPageGrid'
import ImageHero from 'app/theme/ImageHero'
import partyCatering1 from 'assets/img/party-catering-1.jpg'
import partyCatering2 from 'assets/img/party-catering-2.jpg'
import partyCatering3 from 'assets/img/party-catering-3.jpg'
import partyCatering4 from 'assets/img/party-catering-4.jpg'
import partyCateringBanner from 'assets/img/party-catering-hero.jpg'
import LeadModal from 'app/leads/LeadModal'
import getGoogleMetaTags from './occasionSEOData'

import { useModalState } from 'lib/modal'
import NavbarFixed from 'app/theme/NavbarFixed'
import { useInView } from 'react-intersection-observer'


export default function PartyCateringPage() {
  const { openModal, ...modalState } = useModalState()

  const [ref, inView] = useInView({
    rootMargin: '1px',
    initialInView: true,
  })

  const googleMetaTags = getGoogleMetaTags('party')

  return (
    <Layout header={<Header occasionMenu />}>
      <Helmet>
        <title>Food Truck Catering for Parties (with free cost calculator) - Roaming Hunger</title>
        <meta name="description" content="Cater with food trucks for any type of event, including birthdays, graduation parties, house warming, anniversaries and Bar/Bat Mitzvahs."></meta>
        <meta name="og:title" content="Food Truck Catering for Parties (with free cost calculator)"></meta>
        <script type="application/ld+json">
          {googleMetaTags}
        </script>
      </Helmet>
      <LeadModal
        {...modalState}
      />
      <div ref={ref}>
        <ImageHero
          imageUrl={partyCateringBanner}
          title="Food Truck Party Catering"
          onButtonClick={openModal}
          description="Whether you're celebrating a birthday, graduation, Bar/Bat Mitzvah, anniversary or holiday, food trucks make your party more memorable."
        />
      </div>
      {!inView && (
        <NavbarFixed
          onButtonClick={openModal}
        />
      )}
      <IntroGrid />
      <WhyWorkWithUs onButtonClick={openModal}/>
      <WhyWorkWithUsGrid />
      <CuisineTypes baseUrl='/food-truck-catering' />
      <HowItWorks heading="How Private Party Catering Works" onButtonClick={openModal}/>
      <HowItWorksLarge heading="How Private Party Catering Works" onButtonClick={openModal} />
      <FeaturedClients onButtonClick={openModal} />
      <Calculator onButtonClick={openModal}/>
      <FAQ/>
      <OccasionsSlider className="py-12 bg-white" heading="Other catering occasions" onButtonClick={openModal}/>
    </Layout>
  )
}
function IntroGrid() {
  return (
    <OccasionPageGrid className="pt-10">
      <OccasionPageGridRow
        heading="RSVPs Guaranteed"
        content="Make great food the centerpiece of your party. Choose from thousands of cuisine options and let guests customize individual orders during your event. Round up your friends, family and neighbors, because better food makes for better memories."
        image={partyCatering1}
        reverse
      />
      <OccasionPageGridRow
        heading="Save the Stress"
        content="Sit back, enjoy the festivities, watch your guests dive into a delicious meal and then watch the mess drive away. We simplify the steps of booking the best gourmet food trucks so your party catering is covered. Being the host with the most has never been easier."
        image={partyCatering2}
      />
    </OccasionPageGrid>
  )
}

function WhyWorkWithUsGrid() {
  return (
    <OccasionPageGrid className="pb-10">
      <OccasionPageGridRow
        heading="There's No Party Like a Food Truck Party"
        content="Transform your party into a can't-miss event. With a festive, on-trend food truck bringing the best bites to your front door, partygoers will come for the food and stay for the company."
        image={partyCatering3}
        reverse
      />
      <OccasionPageGridRow
        heading="We've Got You Covered"
        content="Book your event with confidence knowing we have established relationships with the food trucks. We hold all funds until after your event, so we can guarantee you will get fantastic service every time."
        image={partyCatering4}
      />
    </OccasionPageGrid>
  )
}

function FAQ() {
  return (
    <div className="bg-navy">
      <div className="container p-6">
        <div className="flex w-full">
          <div className="w-full py-16">
            <h2 className="text-white text-4xl leading-none font-platform text-center">
              Frequently Asked Questions
            </h2>
            <div className="w-full md:w-4/5 mb-2 mt-6 m-auto">
              <h3 className="text-white text-3xl">How much does food truck catering for parties cost?</h3>
              <div className='text-white text-lg'>
                <p className="mt-1 font-light">
                  Food truck catering packages typically range from $1,000-$3,000, depending on guest count, cuisine, and
                  length of service.
                </p>
                <p className="mt-1 font-light">
                  Cuisine affects the costs of your food truck catering more than any other factor. Here
                  are typical per-person prices along with average minimums.
                </p>
                <table className="w-full table-auto border-spacing-px border-collapse border border-navy-300 my-4">
                  <thead>
                    <tr>
                      <th className="text-left p-2 border border-navy-300">Cuisine</th>
                      <th className="text-left p-2 border border-navy-300">Average Price per Person</th>
                      <th className="text-left p-2 border border-navy-300">1-100 Eaters (Minimum)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">
                        <a href="/food-truck-catering/taco-truck-catering/" target="blank" className="text-blue-400">Taco Truck Catering</a>
                      </td>
                      <td className="p-2 border border-navy-300 font-light">$15-25</td>
                      <td className="p-2 border border-navy-300 font-light">$1,200</td>
                    </tr>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">
                        <a href="/food-truck-catering/bbq-food-truck-catering/" target="blank" className="text-blue-400">BBQ Truck Catering</a>
                      </td>
                      <td className="p-2 border border-navy-300 font-light">$21-34</td>
                      <td className="p-2 border border-navy-300 font-light">$1,500</td>
                    </tr>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">
                        <a href="/food-truck-catering/burger-catering-truck/" target="blank" className="text-blue-400">Burger Truck Catering</a>
                      </td>
                      <td className="p-2 border border-navy-300 font-light">$16-27</td>
                      <td className="p-2 border border-navy-300 font-light">$1,500</td>
                    </tr>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">
                        <a href="/food-truck-catering/pizza-truck-catering/" target="blank" className="text-blue-400">Pizza Truck Catering</a>
                      </td>
                      <td className="p-2 border border-navy-300 font-light">$20-28</td>
                      <td className="p-2 border border-navy-300 font-light">$1,500</td>
                    </tr>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">Mediterranean Truck Catering</td>
                      <td className="p-2 border border-navy-300 font-light">$18-28</td>
                      <td className="p-2 border border-navy-300 font-light">$1,500</td>
                    </tr>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">Asian Truck Catering</td>
                      <td className="p-2 border border-navy-300 font-light">$18-28</td>
                      <td className="p-2 border border-navy-300 font-light">$1,500</td>
                    </tr>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">Indian Truck Catering</td>
                      <td className="p-2 border border-navy-300 font-light">$18-28</td>
                      <td className="p-2 border border-navy-300 font-light">$1,500</td>
                    </tr>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">Dessert Truck Catering</td>
                      <td className="p-2 border border-navy-300 font-light">$10-20</td>
                      <td className="p-2 border border-navy-300 font-light">$800</td>
                    </tr>
                    <tr>
                      <td className="p-2 border border-navy-300 font-light">Beverage Truck Catering</td>
                      <td className="p-2 border border-navy-300 font-light">$8-18</td>
                      <td className="p-2 border border-navy-300 font-light">$800</td>
                    </tr>
                  </tbody>
                </table>
                <p className="mt-1 font-light">
                  Use our <a href="#calculator" className="text-blue-400 underline">Food Truck Catering Cost Calculator</a>
                </p>
                <p className="mt-1 font-light">
                  Adding drinks or dessert options (with a savory food truck) will increase the price per
                  person by $1-$5.
                </p>
                <p className="mt-1 font-light">
                  If you're having a small party of 50 guests or less (birthday, anniversary, etc.), you'll probably be affected by the
                  food truck catering minimums, and the price per person will increase.
                </p>
                <p className="mt-1 font-light">
                  Typical service times for a party are 2-3 hours. Additional hours will increase total costs. Travel
                  fees can also affect pricing. Food trucks may charge a per-mile fee if your event is
                  outside their normal travel radius (20-30 miles).
                </p>
                <p className="mt-1 font-light">
                  This data is based on historical Roaming Hunger catering bookings and does not include
                  any taxes or fees.
                </p>
              </div>

              <h3 className="text-white text-3xl mt-4">What are some popular cuisines for food truck party catering?</h3>
              <div className='text-white text-lg'>
                <p className="mt-1 font-light">
                  Here are our top 10 most requested food truck cuisines by people planning to hire a food truck for their party:
                </p>
                <ul className="pl-2 list-disc list-inside">
                  <li className="font-light">
                    <a href="/food-truck-catering/taco-truck-catering/" target="blank" className="text-blue-400">Tacos</a> (Mexican and Fusion)
                  </li>
                  <li className="font-light">
                    <a href="/food-truck-catering/burger-catering-truck/" target="blank" className="text-blue-400">Burgers</a>
                  </li>
                  <li className="font-light">
                    <a href="/food-truck-catering/bbq-food-truck-catering/" target="blank" className="text-blue-400">BBQ</a>
                  </li>
                  <li className="font-light">
                    <a href="/food-truck-catering/pizza-truck-catering/" target="blank" className="text-blue-400">Pizza</a>
                  </li>
                  <li className="font-light">Sandwiches</li>
                  <li className="font-light">Asian</li>
                  <li className="font-light">Dessert&nbsp;</li>
                  <li className="font-light">Healthy Food</li>
                  <li className="font-light">Mediterranean</li>
                  <li className="font-light">Breakfast</li>
                </ul>
                <p className="mt-1 font-light">
                  We also get many requests for Vegetarian and Vegan options, which many food trucks
                  can accommodate.
                </p>
              </div>
              <h3 className="text-white text-3xl mt-4">How do I get party catering packages for food trucks near me?</h3>
              <div className='text-white text-lg'>
                <p className="mt-1 font-light">
                  If your event details are confirmed, and your budget meets the minimum (see above), you can
                  get catering packages and quotes by submitting a catering request.
                </p>
                <p className="mt-1 font-light">
                  Roaming Hunger will send your request to food trucks in your area, and available trucks will
                  submit a party catering package with pricing and menu. From there you can choose the best catering
                  package for you.
                </p>
              </div>
              <h3 className="text-white text-3xl mt-4">Why should I use Roaming Hunger?</h3>
              <div className='text-white text-lg'>
                <p className="mt-1 font-light">
                  Roaming Hunger is the largest website for food trucks and mobile food businesses. Since 2009,
                  we’ve helped thousands of companies and individuals book food trucks for catering and events.
                  Today, we have over 23,000 independent food trucks, trailers, carts, and pop-ups on our site.
                </p>
                <ol className="pl-6 list-disc list-outside">
                  <li className="font-light"><strong className="font-black">Variety</strong> - Our network consists of hundreds of local vendors in every major city, totaling 23,000+ nationally.</li>
                  <li className="font-light"><strong className="font-black">Save Time</strong> - Why have 100 conversations to find one food truck when you can have one conversation to find 100 food trucks?!</li>
                  <li className="font-light"><strong className="font-black">Speed</strong> - Whether you are ready to book one day or one month out from your event, we will provide every available food truck option.</li>
                  <li className="font-light"><strong className="font-black">Quality Options</strong> - All the food trucks we work with are gourmet, vetted, licensed, proven and reliable.</li>
                  <li className="font-light"><strong className="font-black">Service</strong> - Every catering is protected by our 100% Roaming Hunger Service Guarantee. If the caterer does not show, we’ll get you a replacement caterer or refund 100% of your money.</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
